// Path management hooks and functions
import { useState, useCallback, useRef, useEffect } from 'react';
import { contextDebug } from '../../utils/debug';
// Use root-level imports to avoid circular dependencies
import { 
  isAutoMode, 
  isCustomMode, 
  isSetupMode
} from '../../Services';

// Path management hook
export const usePathManagement = (locations) => {
  // Path storage for different path modes
  const [autoPath, setAutoPath] = useState([]);
  const [customPath, setCustomPath] = useState([]);
  
  // Used to prevent circular updates
  const prevAutoPathRef = useRef(null);
  const prevCustomPathRef = useRef(null);
  const isSavingRef = useRef(false);
  
  // Save auto path
  const saveAutoPath = useCallback((path) => {
    prevAutoPathRef.current = path ? [...path] : null;
  }, []);

  // Save custom path
  const saveCustomPath = useCallback((path) => {
    prevCustomPathRef.current = path ? [...path] : null;
  }, []);

  // Set path for current mode
  const setPath = useCallback((path, currentMode, isFullPath = false) => {
    contextDebug('Setting path', { path, currentMode, isFullPath });
    
    // Add _isFullPath flag if needed
    let pathWithFlag = path;
    if (Array.isArray(path) && path.length > 0) {
      pathWithFlag = [...path];
      pathWithFlag._isFullPath = isFullPath;
    }
    
    if (isAutoMode(currentMode)) {
      setAutoPath(pathWithFlag);
      // Update ref
      if (pathWithFlag && pathWithFlag.length > 1) {
        saveAutoPath(pathWithFlag);
      }
    } else if (isCustomMode(currentMode)) {
      setCustomPath(pathWithFlag);
      // Update ref
      if (pathWithFlag && pathWithFlag.length > 1) {
        saveCustomPath(pathWithFlag);
      }
    } else if (isSetupMode(currentMode)) {
      // Clear paths in setup mode
      setAutoPath([]);
      setCustomPath([]);
    }
  }, [saveAutoPath, saveCustomPath]);

  return {
    autoPath,
    setAutoPath,
    customPath,
    setCustomPath,
    prevAutoPathRef,
    prevCustomPathRef,
    setPath,
    saveAutoPath,
    saveCustomPath
  };
};

// Get active path for current mode
export const getActivePathByMode = (currentMode, autoPath, customPath) => {
  if (isAutoMode(currentMode)) {
    return autoPath;
  } else if (isCustomMode(currentMode)) {
    return customPath;
  }
  return [];
};

 